/**
 * Disposable.ts
 * @author Diao Zheng
 * @file Represents an object that can be disposed once.
 */
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var _disposed, _disposeFunction;
import { invariant } from "./Contract";
import { devOnly } from "./Dev";
const DISPOSE = Symbol.for("nasi/DISPOSE");
const IS_DISPOSED = Symbol.for("nasi/IS_DISPOSED");
const DISPOSABLE = Symbol.for("nasi/DISPOSABLE");
function isCustomDisposable(disposable) {
    const suspect = disposable;
    return (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    typeof suspect[IS_DISPOSED] !== "undefined" &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        typeof suspect[DISPOSE] === "function");
}
export class Disposable {
    constructor(disposeFunction) {
        _disposed.set(this, false);
        _disposeFunction.set(this, void 0);
        __classPrivateFieldSet(this, _disposeFunction, disposeFunction);
    }
    static tryDispose(disposable) {
        if (typeof disposable === "object" &&
            disposable !== null && // ye olde JavaScript bug
            (disposable instanceof Disposable ||
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                typeof disposable[DISPOSE] === "function" ||
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                typeof disposable[DISPOSABLE] !== "undefined")) {
            Disposable.dispose(disposable);
        }
    }
    static dispose(disposable) {
        if (disposable instanceof Disposable) {
            disposable.dispose();
        }
        else if (isCustomDisposable(disposable)) {
            if (!disposable[IS_DISPOSED]) {
                disposable[DISPOSE]();
            }
            else {
                invariant(() => !disposable[IS_DISPOSED], "Disposable: Attempting to dispose of a disposed object.", false);
            }
        }
        else {
            Disposable.dispose(disposable[DISPOSABLE]);
        }
    }
    /**
     * Execute the internally stored dispose function once and only once.
     */
    dispose() {
        if (!__classPrivateFieldGet(this, _disposed)) {
            __classPrivateFieldSet(this, _disposed, true);
            __classPrivateFieldGet(this, _disposeFunction).call(this);
        }
        else {
            devOnly(() => {
                // eslint-disable-next-line no-console
                console.warn(`Disposable: trying to call dispose() on an already disposed object.`);
            });
        }
    }
    get isDisposed() {
        return __classPrivateFieldGet(this, _disposed);
    }
}
_disposed = new WeakMap(), _disposeFunction = new WeakMap();
Disposable.Dispose = DISPOSE;
Disposable.IsDisposed = IS_DISPOSED;
Disposable.Instance = DISPOSABLE;
